import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { Icon } from '../components/icons';
import { Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { fmtDate } from '../format';
import { useI18n } from '../i18n';
import type { Role, User } from '../types';

const ROLES: Role[] = ['technician', 'lead', 'office', 'owner'];
export const SYSTEMS = ['Brähler DIGIMIC', 'Brähler INFRACOM IR', 'Interpreter booths', 'DS24T stands', 'Sound / PA', 'Video / streaming'];

export function CrewPage() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const isOwner = me?.role === 'owner';
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') });
  const order: Record<Role, number> = { lead: 0, technician: 1, office: 2, owner: 3 };
  const list = (users.data ?? []).filter((u) => showInactive || u.active).sort((a, b) => order[a.role] - order[b.role] || a.name.localeCompare(b.name));
  const counts = (users.data ?? []).filter((u) => u.active).reduce<Record<string, number>>((a, u) => ({ ...a, [u.role]: (a[u.role] ?? 0) + 1 }), {});
  return (
    <>
      <PageHead
        title={t('crew.title')}
        sub={t('crew.sub', { leads: counts.lead ?? 0, techs: counts.technician ?? 0 })}
        actions={
          isOwner && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon.plus />
              {t('crew.new')}
            </button>
          )
        }
      />
      <div className="toolbar">
        <label className="checkbox">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('crew.showInactive')}
        </label>
      </div>
      <div className="card table-wrap">
        {users.isLoading ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty title={t('crew.empty')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('crew.name')}</th>
                <th>{t('crew.role')}</th>
                <th>{t('crew.certifiedSystems')}</th>
                <th>{t('clients.phone')}</th>
                <th>{t('login.email')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className="clickable" onClick={() => setEditing(u)} style={{ opacity: u.active ? 1 : 0.55 }}>
                  <td><strong>{u.name}</strong>{!u.active && <span className="badge" style={{ marginLeft: 6 }}>{t('crew.inactive')}</span>}</td>
                  <td><span className={`badge ${u.role === 'lead' ? 'badge-ink' : ''}`}>{t(`role.${u.role}`)}</span></td>
                  <td className="small">{(u.certifications ?? []).map((c) => c.system).join(', ') || <span className="muted">–</span>}</td>
                  <td>{u.phone && <a href={`tel:${u.phone}`} onClick={(e) => e.stopPropagation()}>{u.phone}</a>}</td>
                  <td className="muted">{u.email}</td>
                  <td className="right"><button className="btn btn-sm">{t('common.open')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <PersonModal person={editing === 'new' ? undefined : editing} canEdit={isOwner} onClose={() => setEditing(null)} />}
    </>
  );
}

function PersonModal({ person, canEdit, onClose }: { person?: User; canEdit: boolean; onClose: () => void }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [f, setF] = useState({
    name: person?.name ?? '',
    email: person?.email ?? '',
    phone: person?.phone ?? '',
    role: person?.role ?? ('technician' as Role),
    language: person?.language ?? 'sq',
    active: person?.active ?? true,
    password: '',
  });
  const [cert, setCert] = useState({ system: '', certifiedOn: '', trainer: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });
  const save = useMutation({
    mutationFn: () => {
      if (!person) return api.post('/users', { name: f.name, email: f.email, phone: f.phone || null, role: f.role, language: f.language, password: f.password });
      return api.patch(`/users/${person.id}`, {
        name: f.name,
        phone: f.phone || null,
        role: f.role,
        language: f.language,
        active: f.active,
        ...(f.password ? { password: f.password } : {}),
      });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const addCert = useMutation({
    mutationFn: () => api.post(`/users/${person!.id}/certifications`, { system: cert.system, certifiedOn: cert.certifiedOn || null, trainer: cert.trainer || null }),
    onSuccess: () => {
      setCert({ system: '', certifiedOn: '', trainer: '' });
      invalidate();
    },
  });
  const delCert = useMutation({ mutationFn: (id: string) => api.del(`/certifications/${id}`), onSuccess: invalidate });
  const fresh = qc.getQueryData<User[]>(['users'])?.find((u) => u.id === person?.id) ?? person;
  const self = person?.id === me?.id;

  return (
    <Modal
      wide
      title={person ? person.name : t('crew.new')}
      onClose={onClose}
      footer={
        canEdit ? (
          <>
            <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={!f.name || !f.email || (!person && f.password.length < 8) || save.isPending} onClick={() => save.mutate()}>
              {t('common.save')}
            </button>
          </>
        ) : (
          <button className="btn" onClick={onClose}>{t('common.close')}</button>
        )
      }
    >
      <ErrorBox error={save.error} />
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="form-grid">
          <Field label={t('crew.name')} htmlFor="p-name">
            <input id="p-name" className="input" value={f.name} onChange={set('name')} autoFocus={!person} />
          </Field>
          <Field label={t('login.email')} hint={person ? t('crew.emailFixed') : undefined} htmlFor="p-email">
            <input id="p-email" className="input" type="email" value={f.email} onChange={set('email')} disabled={Boolean(person)} />
          </Field>
          <Field label={t('clients.phone')} htmlFor="p-phone">
            <input id="p-phone" className="input" type="tel" value={f.phone} onChange={set('phone')} />
          </Field>
          <Field label={t('crew.role')} hint={t(`roleHint.${f.role}`)} htmlFor="p-role">
            <select id="p-role" className="select" value={f.role} onChange={set('role')} disabled={self}>
              {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
            </select>
          </Field>
          <Field label={t('settings.language')} htmlFor="p-lang">
            <select id="p-lang" className="select" value={f.language} onChange={set('language')}>
              <option value="sq">Shqip</option>
              <option value="en">English</option>
            </select>
          </Field>
          <Field label={person ? t('crew.newPassword') : t('login.password')} hint={person ? t('crew.newPasswordHint') : t('crew.passwordHint')} htmlFor="p-pw">
            <input id="p-pw" className="input" type="text" autoComplete="new-password" value={f.password} onChange={set('password')} />
          </Field>
          {person && !self && (
            <div className="full">
              <label className="checkbox">
                <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
                {t('crew.activeLabel')}
              </label>
            </div>
          )}
        </div>
      </fieldset>

      {person && (
        <div className="stack" style={{ gap: 10, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <h3>{t('crew.certifications')}</h3>
          <ErrorBox error={addCert.error ?? delCert.error} />
          {(fresh?.certifications ?? []).length === 0 ? (
            <span className="muted small">{t('crew.noCerts')}</span>
          ) : (
            <table className="table">
              <tbody>
                {(fresh?.certifications ?? []).map((c) => (
                  <tr key={c.id}>
                    <td><strong>{c.system}</strong></td>
                    <td className="muted">{c.certifiedOn ? fmtDate(c.certifiedOn, lang) : ''}</td>
                    <td className="muted">{c.trainer}</td>
                    <td className="right"><button className="btn btn-sm btn-ghost" onClick={() => delCert.mutate(c.id)}>{t('common.remove')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <Field label={t('crew.system')} htmlFor="cert-sys">
              <input id="cert-sys" className="input" list="sys-list" value={cert.system} onChange={(e) => setCert({ ...cert, system: e.target.value })} />
              <datalist id="sys-list">{SYSTEMS.map((s) => <option key={s} value={s} />)}</datalist>
            </Field>
            <Field label={t('crew.certifiedOn')} htmlFor="cert-date">
              <input id="cert-date" className="input" type="date" value={cert.certifiedOn} onChange={(e) => setCert({ ...cert, certifiedOn: e.target.value })} />
            </Field>
            <Field label={t('crew.trainer')} htmlFor="cert-tr">
              <input id="cert-tr" className="input" value={cert.trainer} onChange={(e) => setCert({ ...cert, trainer: e.target.value })} />
            </Field>
            <button className="btn" disabled={!cert.system || addCert.isPending} onClick={() => addCert.mutate()}>{t('common.add')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
